/**
 * Participant roles + display helpers for multi-party conversations.
 *
 * A session has one or more salespeople (team-selling calls) and zero or more
 * customers. `role` (the `speaker` field on a transcript) is the routing/analysis
 * axis; individual identity lives on `participantId` + `speakerName`, plus the
 * session-level `participants` roster. This module is the single source of truth
 * for role constants, the per-participant color palettes, and the label/color
 * helpers that every transcript renderer shares.
 */

export const ParticipantRole = {
  SALESPERSON: 'salesperson',
  CUSTOMER: 'customer',
} as const;

// Deliberate value/type merge (enum-like const object + union type of its values).
// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ParticipantRole = (typeof ParticipantRole)[keyof typeof ParticipantRole];

export const ParticipantSource = {
  RECALL: 'recall',
  MANUAL: 'manual',
  DIARIZATION: 'diarization',
  /** A rep who joined a desktop call with their own mic (co-seller): always a salesperson. */
  STANDALONE_JOIN: 'standalone_join',
} as const;

// eslint-disable-next-line @typescript-eslint/no-redeclare
export type ParticipantSource =
  (typeof ParticipantSource)[keyof typeof ParticipantSource];

export interface Participant {
  participantId?: string;
  name?: string;
  role: ParticipantRole;
  source?: ParticipantSource;
  externalId?: string;
}

/**
 * Copy for a co-seller's row in the Participants editor (a rep who joined the
 * call with their own mic). The backend refuses a role change on that row.
 */
export const CO_SELLER_PARTICIPANT_COPY = {
  ownMicBadge: 'Own mic',
  roleLocked: 'Joined with their own mic, so always a salesperson.',
} as const;

/** Whether a roster row is a rep who joined with their own mic (role locked to salesperson). */
export const isOwnMicParticipant = (participant?: Pick<Participant, 'source'> | null): boolean =>
  participant?.source === ParticipantSource.STANDALONE_JOIN;

/** Minimal shape any transcript / detection entry exposes for display. */
export interface SpeakerLike {
  speaker?: string; // role: 'salesperson' | 'customer'
  speakerName?: string;
  participantId?: string;
}

export interface SpeakerColor {
  bg: string;
  text: string;
  border: string;
}

/**
 * Distinct shades of blue assigned to salespeople by their order in the roster
 * (team-selling calls can have more than one). All use white text — the bubbles
 * are solid blue. Wraps around if there are more salespeople than palette entries.
 */
export const SALESPERSON_COLOR_PALETTE: SpeakerColor[] = [
  { bg: '#3B82F6', text: '#FFFFFF', border: '#2563EB' }, // blue-500
  { bg: '#1D4ED8', text: '#FFFFFF', border: '#1E40AF' }, // blue-700
  { bg: '#0EA5E9', text: '#FFFFFF', border: '#0284C7' }, // sky-500
  { bg: '#4F46E5', text: '#FFFFFF', border: '#4338CA' }, // indigo-600
  { bg: '#0891B2', text: '#FFFFFF', border: '#0E7490' }, // cyan-600
  { bg: '#2563EB', text: '#FFFFFF', border: '#1D4ED8' }, // blue-600
];

/** Primary salesperson color (first palette slot) — kept for direct references. */
export const SALESPERSON_COLOR: SpeakerColor = SALESPERSON_COLOR_PALETTE[0];

/**
 * Stable, visually distinct colors assigned to customers by their order in the
 * roster. Wraps around if there are more customers than palette entries.
 */
export const CUSTOMER_COLOR_PALETTE: SpeakerColor[] = [
  { bg: '#F3F4F6', text: '#111827', border: '#D1D5DB' }, // slate
  { bg: '#EDE9FE', text: '#5B21B6', border: '#C4B5FD' }, // violet
  { bg: '#DCFCE7', text: '#166534', border: '#86EFAC' }, // green
  { bg: '#FFEDD5', text: '#9A3412', border: '#FDBA74' }, // orange
  { bg: '#FEE2E2', text: '#991B1B', border: '#FCA5A5' }, // red
  { bg: '#CFFAFE', text: '#155E75', border: '#67E8F9' }, // cyan
  { bg: '#FEF9C3', text: '#854D0E', border: '#FDE047' }, // amber
];

const isSalespersonRole = (role?: string): boolean =>
  role === ParticipantRole.SALESPERSON;

/**
 * Ordered list of customer participant ids (roster order = first-seen order).
 * Used to assign each customer a stable palette slot.
 */
const customerIds = (roster?: Participant[]): string[] =>
  (roster || [])
    .filter((p) => p?.role === ParticipantRole.CUSTOMER && p?.participantId)
    .map((p) => p.participantId as string);

const salespersonIds = (roster?: Participant[]): string[] =>
  (roster || [])
    .filter((p) => p?.role === ParticipantRole.SALESPERSON && p?.participantId)
    .map((p) => p.participantId as string);

/**
 * 0-based index of a customer among all customers in the roster. Falls back to 0
 * when the participant is unknown (e.g. legacy single-customer sessions).
 */
export const customerIndex = (
  participantId?: string,
  roster?: Participant[],
): number => {
  if (!participantId) return 0;
  const idx = customerIds(roster).indexOf(participantId);
  return idx >= 0 ? idx : 0;
};

/**
 * 0-based index of a salesperson among all salespeople in the roster. Falls back
 * to 0 for the common single-salesperson case (or a legacy entry with no id).
 */
export const salespersonIndex = (
  participantId?: string,
  roster?: Participant[],
): number => {
  if (!participantId) return 0;
  const idx = salespersonIds(roster).indexOf(participantId);
  return idx >= 0 ? idx : 0;
};

/** Color for a transcript/detection entry, given the session roster. */
export const getSpeakerColor = (
  entry: SpeakerLike,
  roster?: Participant[],
): SpeakerColor => {
  if (isSalespersonRole(entry?.speaker)) {
    const idx = salespersonIndex(entry?.participantId, roster);
    return SALESPERSON_COLOR_PALETTE[idx % SALESPERSON_COLOR_PALETTE.length];
  }
  const idx = customerIndex(entry?.participantId, roster);
  return CUSTOMER_COLOR_PALETTE[idx % CUSTOMER_COLOR_PALETTE.length];
};

/**
 * Display label for a transcript/detection entry.
 * - salesperson → the ROSTER name for this participantId (a real profile name
 *   for a live session's creator, or a manually-entered name for a 2nd+
 *   salesperson who joined/was reclassified), falling back to
 *   `opts.salespersonLabel` (default "Salesperson") when the roster has no
 *   real name to offer — e.g. legacy sessions with no participantId on the entry.
 * - customer → the ROSTER name (authoritative — reflects live renames), else the
 *   entry's own `speakerName`, else "Customer N" from roster position.
 *
 * The roster is preferred over the per-entry `speakerName` so that when the
 * salesperson renames a customer, every one of that customer's transcripts
 * (already-received and future) updates immediately — each transcript carries a
 * frozen name at receive time, but the roster is the single editable source.
 */
export const getSpeakerLabel = (
  entry: SpeakerLike,
  roster?: Participant[],
  opts?: { salespersonLabel?: string },
): string => {
  if (isSalespersonRole(entry?.speaker)) {
    const pid = entry?.participantId || ParticipantRole.SALESPERSON;
    const rosterName = (roster || []).find((p) => p?.participantId === pid)?.name;
    if (rosterName && rosterName.trim() && rosterName.trim() !== 'Salesperson') {
      return rosterName;
    }
    return opts?.salespersonLabel || 'Salesperson';
  }
  const rosterName = entry?.participantId != null
    ? (roster || []).find((p) => p?.participantId === entry.participantId)?.name
    : undefined;
  if (rosterName && rosterName.trim()) return rosterName;
  if (entry?.speakerName && entry.speakerName.trim()) return entry.speakerName;
  return `Customer ${customerIndex(entry?.participantId, roster) + 1}`;
};

/**
 * Multi-party post-call analysis (callInsights.multiParty). A customer's buying
 * role is INFERRED from the call, so every label carries its hedge.
 */
export const INFERRED_ROLE_LABELS: Record<string, string> = {
  decision_maker: 'Likely decision-maker',
  champion: 'Likely champion',
  technical_evaluator: 'Likely technical evaluator',
  end_user: 'Likely end user',
  influencer: 'Likely influencer',
  gatekeeper: 'Likely gatekeeper',
  blocker: 'Possible blocker',
  unclear: 'Role unclear',
};

export const ENGAGEMENT_LABELS: Record<string, string> = {
  high: 'High engagement',
  medium: 'Medium engagement',
  low: 'Low engagement',
};

/** Tone dimensions compared rep by rep; a negative trait reads as a warning when high. */
export const REP_TONE_DIMENSIONS: { key: 'confidence' | 'optimism' | 'defensiveness' | 'confrontation'; label: string; negative: boolean }[] = [
  { key: 'confidence', label: 'Confidence', negative: false },
  { key: 'optimism', label: 'Optimism', negative: false },
  { key: 'defensiveness', label: 'Defensiveness', negative: true },
  { key: 'confrontation', label: 'Confrontation', negative: true },
];

/** How many people a multi-party panel lists before "Show more" (meetings can have a dozen). */
export const MAX_VISIBLE_PARTICIPANTS = 6;
